import type { MiniAppCatalogRow } from '@/api/client';

export const MINI_APPS_CATALOG_QUERY_KEY = ['miniApps', 'catalog'] as const;

export function compareMiniAppsCatalog(a: MiniAppCatalogRow, b: MiniAppCatalogRow): number {
  if (b.likes_count !== a.likes_count) return b.likes_count - a.likes_count;
  return a.name.localeCompare(b.name, 'ru');
}

export function sortMiniAppsCatalog(items: MiniAppCatalogRow[]): MiniAppCatalogRow[] {
  return [...items].sort(compareMiniAppsCatalog);
}

function repositionMiniAppCatalogRow(
  items: MiniAppCatalogRow[],
  sourceIndex: number,
  nextRow: MiniAppCatalogRow
): MiniAppCatalogRow[] {
  const next = [...items];
  next[sourceIndex] = nextRow;

  let targetIndex = sourceIndex;

  while (targetIndex > 0 && compareMiniAppsCatalog(nextRow, next[targetIndex - 1]) < 0) {
    targetIndex -= 1;
  }

  while (
    targetIndex < next.length - 1 &&
    compareMiniAppsCatalog(nextRow, next[targetIndex + 1]) > 0
  ) {
    targetIndex += 1;
  }

  if (targetIndex === sourceIndex) return next;

  next.splice(sourceIndex, 1);
  next.splice(targetIndex, 0, nextRow);
  return next;
}

export function updateMiniAppCatalogRow(
  prev: MiniAppCatalogRow[] | undefined,
  id: string,
  updater: (row: MiniAppCatalogRow) => MiniAppCatalogRow
): MiniAppCatalogRow[] | undefined {
  if (!prev) return prev;
  const rowIndex = prev.findIndex((row) => row.id === id);
  if (rowIndex < 0) return prev;

  const prevRow = prev[rowIndex];
  const nextRow = updater(prevRow);
  if (nextRow === prevRow) return prev;
  return repositionMiniAppCatalogRow(prev, rowIndex, nextRow);
}

export function replaceMiniAppCatalogRow(
  prev: MiniAppCatalogRow[] | undefined,
  row: MiniAppCatalogRow
): MiniAppCatalogRow[] | undefined {
  return updateMiniAppCatalogRow(prev, row.id, (current) => {
    if (
      current.name === row.name &&
      current.description === row.description &&
      current.type === row.type &&
      current.preview_link === row.preview_link &&
      current.likes_count === row.likes_count &&
      current.liked_by_me === row.liked_by_me
    ) {
      return current;
    }
    return row;
  });
}

export function toggleMiniAppCatalogLikeOptimistically(
  prev: MiniAppCatalogRow[] | undefined,
  id: string,
  nextLiked: boolean
): MiniAppCatalogRow[] | undefined {
  return updateMiniAppCatalogRow(prev, id, (row) => {
    if (row.liked_by_me === nextLiked) return row;

    const nextLikesCount = Math.max(0, row.likes_count + (nextLiked ? 1 : -1));
    return {
      ...row,
      liked_by_me: nextLiked,
      likes_count: nextLikesCount,
    };
  });
}

export function mergeMiniAppLikeCounts(
  prev: MiniAppCatalogRow[] | undefined,
  updates: { id: string; likes_count: number }[]
): MiniAppCatalogRow[] | undefined {
  if (!prev) return prev;

  let next = prev;
  for (const update of updates) {
    next = updateMiniAppCatalogRow(next, update.id, (row) => {
      if (row.likes_count === update.likes_count) return row;
      return { ...row, likes_count: update.likes_count };
    }) ?? next;
  }

  return next;
}
